// Port of SuiteModel/Readability.swift and SentenceLengths.swift: the standard readability formulas and the length of
// every sentence, over prose stored as the dialect (markers ignored; scene breaks and blank paragraphs don't count; a
// paragraph with no closing punctuation still ends a sentence). Presented as description, never a grade of quality.
//
// Exact but for sentence boundaries: see sentences.ts (`Intl.Segmenter` where the Mac uses `NLTokenizer`).
import { plainParagraphs } from '../proseMarkdown.ts';
import { charIsLetter, graphemes } from '../unicode.ts';
import { PROSE_WORD } from './proseWords.ts';
import { charIsLowercase } from './scalars.ts';
import { sentenceRanges } from './sentences.ts';

export interface Readability {
  words: number;
  sentences: number;
  syllables: number;
  /** Words of three or more syllables, not counting capitalised words (names), for Gunning Fog. */
  complexWords: number;
  letters: number;
  /** Sentences of 30 words or more. */
  longSentences: number;
}

export const emptyReadability = (): Readability => ({ words: 0, sentences: 0, syllables: 0, complexWords: 0, letters: 0, longSentences: 0 });
export const addReadability = (a: Readability, b: Readability): Readability => ({
  words: a.words + b.words, sentences: a.sentences + b.sentences, syllables: a.syllables + b.syllables,
  complexWords: a.complexWords + b.complexWords, letters: a.letters + b.letters, longSentences: a.longSentences + b.longSentences,
});

export const isEmptyReadability = (r: Readability) => r.words === 0 || r.sentences === 0;
export const wordsPerSentence = (r: Readability) => (r.sentences === 0 ? 0 : r.words / r.sentences);
export const syllablesPerWord = (r: Readability) => (r.words === 0 ? 0 : r.syllables / r.words);
/** Flesch Reading Ease: higher is easier; most fiction for adults falls between 60 and 90. */
export const fleschReadingEase = (r: Readability) => 206.835 - 1.015 * wordsPerSentence(r) - 84.6 * syllablesPerWord(r);
/** Flesch–Kincaid grade level. */
export const fleschKincaidGrade = (r: Readability) => 0.39 * wordsPerSentence(r) + 11.8 * syllablesPerWord(r) - 15.59;
/** Gunning Fog index. */
export const gunningFog = (r: Readability) => (r.words === 0 ? 0 : 0.4 * (wordsPerSentence(r) + (100 * r.complexWords) / r.words));
/** Coleman–Liau index. */
export const colemanLiau = (r: Readability) =>
  (r.words === 0 ? 0 : (0.0588 * r.letters * 100) / r.words - (0.296 * r.sentences * 100) / r.words - 15.8);

/** Flesch's own description of a Reading Ease score. */
export function fleschBand(r: Readability): string {
  const s = fleschReadingEase(r);
  if (s >= 90) return 'Very easy';
  if (s >= 80) return 'Easy';
  if (s >= 70) return 'Fairly easy';
  if (s >= 60) return 'Plain English';
  if (s >= 50) return 'Fairly difficult';
  if (s >= 30) return 'Difficult';
  return 'Very difficult';
}

/** `Readability.forEachSentence`: each sentence of the prose, in order, with its words (UTF-16 ranges; never empty). */
export function forEachSentence(texts: readonly string[], body: (sentence: string, words: string[]) => void) {
  for (const text of texts) {
    for (const paragraph of plainParagraphs(text)) {
      for (const [a, b] of sentenceRanges(paragraph)) {
        const sentence = paragraph.slice(a, b);
        const words = [...sentence.matchAll(PROSE_WORD)].map((m) => m[0]);
        if (words.length) body(sentence, words);
      }
    }
  }
}

/** `Readability.measure`. */
export function measureReadability(texts: readonly string[]): Readability {
  const r = emptyReadability();
  forEachSentence(texts, (_sentence, words) => {
    r.sentences++;
    r.words += words.length;
    if (words.length >= 30) r.longSentences++;
    for (const word of words) {
      const n = syllables(word);
      r.syllables += n;
      const chars = graphemes(word);
      r.letters += chars.filter(charIsLetter).length; // `Character.isLetter`
      if (n >= 3 && charIsLowercase(chars[0])) r.complexWords++;
    }
  });
  return r;
}

/** `Readability.measureEach`: each text on its own, to sum into chapters and the book. */
export const measureReadabilityEach = (texts: readonly string[]) => texts.map((t) => measureReadability([t]));

const VOWELS = new Set('aeiouy');

/** `Readability.syllables`: runs of vowels, less a silent final "e", "-ed" or "-es". */
export function syllables(word: string): number {
  const w = graphemes(word.toLowerCase()).filter(charIsLetter);
  if (w.length <= 3) return 1;
  let count = 0;
  let inVowel = false;
  for (const c of w) {
    const v = VOWELS.has(c);
    if (v && !inVowel) count++;
    inVowel = v;
  }
  const s = w.join('');
  const consonant = (i: number) => i >= 0 && !VOWELS.has(w[i]);
  if (s.endsWith('e') && !(s.endsWith('le') && consonant(w.length - 3)) && !s.endsWith('ee') && count > 1) {
    count--; // make, fire (but table, agree)
  } else if (s.endsWith('ed') && !'td'.includes(w[w.length - 3]) && count > 1) {
    count--; // walked (but wanted)
  } else if (s.endsWith('es') && !'sxzcg'.includes(w[w.length - 3]) && !s.endsWith('hes') && count > 1) {
    count--; // makes (but boxes, catches)
  }
  return Math.max(1, count);
}

// MARK: Sentence lengths

/** `SentenceLengths`: words per sentence in reading order, split exactly as `Readability` splits them. */
export interface SentenceLengths { lengths: number[] }

/** Sentences at or above this many words share the chart's last bar. */
export const HISTOGRAM_CAP = 50;

export function measureSentenceLengths(texts: readonly string[]): SentenceLengths {
  const lengths: number[] = [];
  forEachSentence(texts, (_s, words) => lengths.push(words.length));
  return { lengths };
}

export const measureSentenceLengthsEach = (texts: readonly string[]) => texts.map((t) => measureSentenceLengths([t]));
export const addSentenceLengths = (a: SentenceLengths, b: SentenceLengths): SentenceLengths => ({ lengths: [...a.lengths, ...b.lengths] });

export const sentenceMean = (s: SentenceLengths) => (s.lengths.length === 0 ? 0 : s.lengths.reduce((a, b) => a + b, 0) / s.lengths.length);

/** Linearly interpolated percentile (`p` in 0…1). */
export function percentile(s: SentenceLengths, p: number): number {
  const sorted = [...s.lengths].sort((a, b) => a - b);
  if (!sorted.length) return 0;
  const position = Math.min(1, Math.max(0, p)) * (sorted.length - 1);
  const lower = Math.trunc(position), upper = Math.min(lower + 1, sorted.length - 1);
  return sorted[lower] + (position - lower) * (sorted[upper] - sorted[lower]);
}

export const sentenceMedian = (s: SentenceLengths) => percentile(s, 0.5);

/** Population standard deviation, in words. */
export function standardDeviation(s: SentenceLengths): number {
  if (s.lengths.length <= 1) return 0;
  const m = sentenceMean(s);
  return Math.sqrt(s.lengths.reduce((acc, n) => acc + (n - m) * (n - m), 0) / s.lengths.length);
}

/** Standard deviation as a share of the average. */
export const variation = (s: SentenceLengths) => (sentenceMean(s) === 0 ? 0 : standardDeviation(s) / sentenceMean(s));

/** Sentences of each length 1…`HISTOGRAM_CAP`; the last entry counts every sentence of the cap or more. */
export function histogram(s: SentenceLengths): number[] {
  const counts = new Array<number>(HISTOGRAM_CAP).fill(0);
  for (const n of s.lengths) counts[Math.min(n, HISTOGRAM_CAP) - 1]++;
  return counts;
}

/** Share of sentences with a length in [lo, hi]. */
export const share = (s: SentenceLengths, lo: number, hi: number) =>
  (s.lengths.length === 0 ? 0 : s.lengths.filter((n) => n >= lo && n <= hi).length / s.lengths.length);
